import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminRequest, getAdminHeaders } from "./adminApi";
import { isBaiduTongjiEnabled } from "../../utils/baiduTongji";

const PERIOD_OPTIONS = [
  { label: "近 7 天", value: 7 },
  { label: "近 30 天", value: 30 },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const baiduEnabled = isBaiduTongjiEnabled();

  useEffect(() => {
    fetchOverview();
  }, [days]);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError("");
      const { res, data: payload } = await adminRequest(
        `/api/admin/analytics/overview?days=${days}`,
        { headers: getAdminHeaders() }
      );
      if (!res.ok) {
        setError(payload?.detail || "加载统计数据失败");
        return;
      }
      setData(payload);
    } catch (err) {
      console.error(err);
      setError("无法连接后台服务");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return <div className="text-gray-400">加载中...</div>;
  }

  const maxDaily = Math.max(
    ...(data?.daily_page_views || []).map((item) => item.count),
    1
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">访问统计</h1>
          <p className="text-gray-500 mt-1 text-sm">
            站内 PV 与资料浏览数据；全站流量报表可配合百度统计查看
          </p>
        </div>
        <div className="flex gap-2">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                days === opt.value
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl bg-red-50 text-red-600 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      <section className="bg-white rounded-2xl shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-3">百度统计</h2>
        {baiduEnabled ? (
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              已接入百度统计脚本。详细来源、地域、设备等报表请登录{" "}
              <a
                href="https://tongji.baidu.com/"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                百度统计控制台
              </a>
              查看。
            </p>
            <p className="text-gray-400">
              环境变量 REACT_APP_BAIDU_TONGJI_ID 已配置，重新 build 后生效。
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-600 space-y-2">
            <p>尚未配置百度统计 ID。</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-500">
              <li>
                在{" "}
                <a
                  href="https://tongji.baidu.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  tongji.baidu.com
                </a>{" "}
                添加网站并获取统计代码中的 ID
              </li>
              <li>
                在 frontend/.env.production 增加：REACT_APP_BAIDU_TONGJI_ID=你的ID
              </li>
              <li>执行 npm run build 并重新部署前端</li>
            </ol>
          </div>
        )}
      </section>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <StatCard title="今日 PV" value={data?.page_views?.today ?? 0} />
        <StatCard
          title={`${days} 日 PV`}
          value={data?.page_views?.period ?? 0}
        />
        <StatCard
          title={`${days} 日独立访客`}
          value={data?.page_views?.unique_visitors_period ?? 0}
        />
        <StatCard title="累计 PV" value={data?.page_views?.total ?? 0} />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        <StatCard title="资料浏览（累计）" value={data?.profile_views?.total ?? 0} />
        <StatCard title="资料浏览（今日）" value={data?.profile_views?.today ?? 0} />
        <StatCard
          title={`资料浏览（${days} 日）`}
          value={data?.profile_views?.period ?? 0}
        />
        <StatCard
          title="7 日独立浏览者"
          value={data?.profile_views?.unique_viewers_7d ?? 0}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            热门页面（{days} 日）
          </h2>
          {data?.top_pages?.length ? (
            <div className="space-y-2">
              {data.top_pages.map((item) => (
                <div
                  key={item.path}
                  className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-sm"
                >
                  <span className="truncate text-gray-700 mr-3">{item.path}</span>
                  <span className="font-bold text-blue-600 shrink-0">
                    {item.views}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">暂无数据，用户访问页面后会自动记录</p>
          )}
        </section>

        <section className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">每日 PV 趋势</h2>
          {data?.daily_page_views?.length ? (
            <div className="space-y-3">
              {data.daily_page_views.map((item) => (
                <div key={item.date}>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{item.date}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(item.count / maxDaily) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">暂无趋势数据</p>
          )}
        </section>
      </div>

      <section className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">最近资料浏览</h2>
        {data?.recent_profile_views?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b">
                  <th className="py-3 pr-4">浏览者</th>
                  <th className="py-3 pr-4">被浏览会员</th>
                  <th className="py-3 pr-4">来源</th>
                  <th className="py-3">时间</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_profile_views.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50">
                    <td className="py-3 pr-4">
                      <Link
                        to={`/admin/users/${row.viewer_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {row.viewer_name}
                      </Link>
                    </td>
                    <td className="py-3 pr-4">
                      <Link
                        to={`/admin/users/${row.target_user_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {row.target_name}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-gray-500">{row.source}</td>
                    <td className="py-3 text-gray-500">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString("zh-CN")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">暂无资料浏览记录</p>
        )}
      </section>
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <p className="text-gray-400 text-sm">{title}</p>
      <p className="text-3xl font-bold text-gray-800 mt-3">{value}</p>
    </div>
  );
}
